/**
 * useRegisterLink — construit le lien vers /register en PRÉSERVANT l'attribution
 * publicitaire (gclid + utm_*) pour ne pas casser l'attribution Google Ads entre
 * donoor.org et dashboard.donoor.org (sous-domaines séparés).
 *
 * Pourquoi : les CTA `to="/register"` en NuxtLink naviguent côté client (SPA) et
 * ne réémettent pas la query. Le server route `server/routes/register.get.ts`
 * ne s'exécute donc que sur une vraie navigation document. Ce composable garantit
 * que la query (gclid, utm) est TOUJOURS jointe au lien /register, quelle que soit
 * la façon dont l'utilisateur y arrive.
 *
 * Source d'attribution (par ordre de priorité) :
 *   1. la query de l'URL courante (?gclid=… encore présent)
 *   2. le first-touch stocké en sessionStorage (`donoor_attribution`, cf app.vue)
 *
 * Usage dans un composant :
 *   const registerLink = useRegisterLink()
 *   <NuxtLink :to="registerLink">Inscrire mon association</NuxtLink>
 */
const ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export function useRegisterLink(basePath = "/register") {
  const route = useRoute();

  return computed(() => {
    const query: Record<string, string> = {};

    // 1) query courante (a priorité — la plus fraîche)
    for (const key of ATTRIBUTION_KEYS) {
      const v = route.query[key];
      if (typeof v === "string" && v) query[key] = v;
    }

    // 2) first-touch en sessionStorage (client only), sans écraser la query courante
    if (import.meta.client) {
      try {
        const stored = sessionStorage.getItem("donoor_attribution");
        if (stored) {
          const attribution = JSON.parse(stored) as Record<string, string>;
          for (const key of ATTRIBUTION_KEYS) {
            if (!query[key] && attribution[key]) query[key] = attribution[key];
          }
        }
      } catch {
        // sessionStorage inaccessible / JSON corrompu → on ignore silencieusement
      }
    }

    return Object.keys(query).length ? { path: basePath, query } : basePath;
  });
}
